import React, {useCallback, useEffect, useRef} from "react";
import {useTheme} from "../context/ThemeContext";

// Fixed resolution the canvas is bucketed into, independent of the element's
// actual on-screen (responsive) width — pointer coordinates are always mapped
// to a column by proportion, so a later window resize can't desync drawing
// coordinates from playback. Comfortably below SHAPE_MAX_POINTS (2000, backend
// rule) and far more resolution than any window size (max 260) needs — the
// backend resamples down via linear interpolation regardless.
const COLS = 240;

// Border comes from .shape-canvas-wrap's CSS, not drawn on the canvas itself.
const PALETTES = {
  light: {
    bg: "#FFFFFF",
    grid: "rgba(203, 213, 225, 0.35)",
    midline: "rgba(148, 163, 184, 0.5)",
    line: "#3B82F6",
    point: "#3B82F6",
    placeholder: "#94A3B8",
  },
  dark: {
    bg: "#131C2E",
    grid: "rgba(148, 163, 184, 0.15)",
    midline: "rgba(148, 163, 184, 0.35)",
    line: "#60A5FA",
    point: "#60A5FA",
    placeholder: "#64748B",
  },
};

interface Props {
  /** Called after each completed stroke (pointerup) and on Clear — NOT on every
   * pointermove, so the parent (and its Submit-button gating) doesn't re-render
   * on every drag frame. Values are normalized 0-100 (0 = bottom, 100 = top);
   * the backend z-normalizes the shape so absolute scale never matters. */
  onChange: (shape: number[]) => void;
}

export interface ShapeCanvasHandle {
  clear: () => void;
}

const ShapeCanvas = React.forwardRef<ShapeCanvasHandle, Props>(function ShapeCanvas({onChange}, ref) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const columnsRef = useRef<(number | null)[]>(new Array(COLS).fill(null));
  const drawingRef = useRef(false);
  const lastColRef = useRef<number | null>(null);
  const {theme} = useTheme();
  const paletteRef = useRef(PALETTES[theme]);
  paletteRef.current = PALETTES[theme];

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const rect = canvas.getBoundingClientRect();
    const palette = paletteRef.current;

    ctx.clearRect(0, 0, rect.width, rect.height);
    ctx.fillStyle = palette.bg;
    ctx.fillRect(0, 0, rect.width, rect.height);

    // Light horizontal/vertical grid for visual reference while drawing.
    ctx.strokeStyle = palette.grid;
    ctx.lineWidth = 1;
    for (let gx = 0; gx <= rect.width; gx += rect.width / 8) {
      ctx.beginPath();
      ctx.moveTo(gx, 0);
      ctx.lineTo(gx, rect.height);
      ctx.stroke();
    }
    for (let gy = 0; gy <= rect.height; gy += rect.height / 4) {
      ctx.beginPath();
      ctx.moveTo(0, gy);
      ctx.lineTo(rect.width, gy);
      ctx.stroke();
    }
    ctx.strokeStyle = palette.midline;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(0, rect.height / 2);
    ctx.lineTo(rect.width, rect.height / 2);
    ctx.stroke();
    ctx.setLineDash([]);

    const points: {x: number; y: number}[] = [];
    columnsRef.current.forEach((v, i) => {
      if (v === null) return;
      points.push({x: (i / (COLS - 1)) * rect.width, y: (1 - v) * rect.height});
    });

    if (points.length === 0) {
      ctx.fillStyle = palette.placeholder;
      ctx.font = "15px Inter, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("Click or tap and drag to draw a price shape", rect.width / 2, rect.height / 2);
      return;
    }

    ctx.strokeStyle = palette.line;
    ctx.lineWidth = 2.5;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.beginPath();
    points.forEach((p, idx) => (idx === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.stroke();

    const last = points[points.length - 1];
    ctx.fillStyle = palette.point;
    ctx.beginPath();
    ctx.arc(last.x, last.y, 4, 0, Math.PI * 2);
    ctx.fill();
  }, []);

  // (Re)size the backing store for the current device pixel ratio on mount and
  // redraw whenever the theme changes (palette swap). Coordinate math for
  // pointer handling always re-reads getBoundingClientRect() live rather than
  // relying on this cached sizing, so a later CSS-driven resize can't desync
  // drawing from playback — only crispness would degrade, not correctness.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext("2d");
    ctx?.scale(dpr, dpr);
    redraw();
  }, [theme, redraw]);

  const setColumnFromEvent = useCallback((clientX: number, clientY: number, fillFromLast: boolean) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const relX = Math.min(Math.max(clientX - rect.left, 0), rect.width);
    const relY = Math.min(Math.max(clientY - rect.top, 0), rect.height);
    const col = Math.round((relX / rect.width) * (COLS - 1));
    const value = 1 - relY / rect.height;

    if (fillFromLast && lastColRef.current !== null && lastColRef.current !== col) {
      // Interpolate every column between the last recorded column and this one
      // (fast pointer movement can skip columns) — t=0 at `from`, t=1 at `col`,
      // regardless of draw direction.
      const from = lastColRef.current;
      const vFrom = columnsRef.current[from] ?? value;
      const c0 = Math.min(from, col);
      const c1 = Math.max(from, col);
      for (let c = c0; c <= c1; c++) {
        const t = (c - from) / (col - from);
        columnsRef.current[c] = vFrom + (value - vFrom) * t;
      }
    } else {
      columnsRef.current[col] = value;
    }
    lastColRef.current = col;
    redraw();
  }, [redraw]);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    lastColRef.current = null;
    setColumnFromEvent(e.clientX, e.clientY, false);
  }, [setColumnFromEvent]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    setColumnFromEvent(e.clientX, e.clientY, true);
  }, [setColumnFromEvent]);

  const emitShape = useCallback(() => {
    const shape = columnsRef.current.filter((v): v is number => v !== null).map((v) => v * 100);
    onChange(shape);
  }, [onChange]);

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    lastColRef.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
    emitShape();
  }, [emitShape]);

  const clear = useCallback(() => {
    columnsRef.current = new Array(COLS).fill(null);
    lastColRef.current = null;
    redraw();
    onChange([]);
  }, [redraw, onChange]);

  React.useImperativeHandle(ref, () => ({clear}), [clear]);

  return (
    <canvas
      ref={canvasRef}
      className="shape-canvas"
      style={{width: "100%", height: "100%", touchAction: "none", cursor: "crosshair"}}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onPointerLeave={(e) => { if (drawingRef.current) handlePointerUp(e); }}
    />
  );
});

export default ShapeCanvas;
